import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button, CampaignCard, Typography } from '@/primer/components';
import { HeroSection, PageColumn, PageGrid, PageLoadingState } from '@/components/layout';
import { useCampaigns } from '@/api';
import { parseDateMs } from '@/utils';
import { deriveCampaignCardState } from './Dashboard.utils';

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <div className="fixed inset-0 flex overflow-y-auto bg-gradient-brand p-5">
      <div className="m-auto flex w-full max-w-sm flex-col items-center gap-4 text-center">
        <Typography variant="display" size="medium" as="h1">
          No active programs... yet.
        </Typography>
        <img src="/assets/images/illustrations/fallback.svg" alt="" className="h-auto w-full" />
        <Typography variant="body" size="small" color="body">
          You’ll be invited to join a Program as soon as a brand matches your social profile. Keep
          an eye on your inbox!
        </Typography>
      </div>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function Dashboard() {
  const navigate = useNavigate();

  // ── Data ─────────────────────────────────────────────────────────────────

  const {
    data: campaignResponse,
    isLoading: isLoadingCampaigns,
    error: campaignsError,
    refetch: refetchCampaigns,
  } = useCampaigns();

  // ── Derived state ─────────────────────────────────────────────────────────

  const allCampaigns = campaignResponse?.results ?? [];

  // Schedules a client-side re-render at each invitation expiry deadline in turn
  // so the campaign card badges update without requiring a manual page refresh.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const currentTime = Date.now();

    const campaigns = campaignResponse?.results ?? [];
    const nextExpiry = campaigns.reduce<number | null>((earliest, c) => {
      const ms = c.invitationExpiresAt ? parseDateMs(c.invitationExpiresAt) : null;
      if (ms === null || ms <= currentTime) return earliest;
      return earliest === null ? ms : Math.min(earliest, ms);
    }, null);
    if (nextExpiry === null) return;
    const id = setTimeout(() => setNow(Date.now()), nextExpiry - currentTime);
    return () => clearTimeout(id);
  }, [campaignResponse, now]);

  if (isLoadingCampaigns) {
    return <PageLoadingState />;
  }

  if (!isLoadingCampaigns && !campaignsError && allCampaigns.length === 0) {
    return <EmptyState />;
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <HeroSection className="relative isolate">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-svh bg-gradient-brand"
      />
      <PageGrid className="px-5 pt-7">
        <PageColumn className="pb-4 flex flex-col gap-6">
          <Typography variant="display" size="medium" as="h1">
            Your Programs
          </Typography>
          {campaignsError ? (
            <div className="flex flex-col items-center py-8 gap-4">
              <Typography variant="body" size="medium" color="error">
                Failed to load programs
              </Typography>
              <Button variant="secondary" onClick={() => refetchCampaigns()}>
                Retry
              </Button>
            </div>
          ) : (
            // Plain CSS grid inside PageColumn — 1-up on mobile, 2-up on tablet+.
            // gap-5 = var(--spacing-5) = 24px, consistent with grid-primer gutter.
            // Do NOT use grid-primer here — it would add unwanted padding-inline
            // inside the already-constrained PageColumn.
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {allCampaigns.map((campaign) => {
                const {
                  isAvailable,
                  needsToSelectOrClaim,
                  isCompleted,
                  isClaimedAndParticipating,
                  isAwaitingShipment,
                  ...cardProps
                } = deriveCampaignCardState(campaign, now);
                return (
                  <CampaignCard
                    key={campaign.id}
                    {...cardProps}
                    onViewProposal={() => {
                      if (isAvailable || needsToSelectOrClaim) {
                        void navigate({
                          to: '/campaign/$id/select-product',
                          params: { id: campaign.id },
                        });
                      } else if (isClaimedAndParticipating && isAwaitingShipment) {
                        void navigate({
                          to: '/campaign/$id/accepted',
                          params: { id: campaign.id },
                        });
                      } else if (isCompleted || isClaimedAndParticipating) {
                        void navigate({
                          to: '/campaign/$id/tasks',
                          params: { id: campaign.id },
                        });
                      } else {
                        void navigate({
                          to: '/campaign/$id',
                          params: { id: campaign.id },
                        });
                      }
                    }}
                    as="article"
                    shadow="ground"
                  />
                );
              })}
            </div>
          )}
        </PageColumn>
      </PageGrid>
    </HeroSection>
  );
}

export default Dashboard;
